import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Select, Button, Card } from '@one-snap/sparkles';
import { PasswordFieldControl } from '@one-snap/forms';
import {
  Box,
  FormControl,
  MenuItem,
  InputLabel,
  Snackbar,
  Alert
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import getDefaultCredentials from '../../config/defaultCredentials';
import env from '../../config/env';
import './IndexPage.css';

function IndexPage() {
  const navigate = useNavigate();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const methods = useForm({
    defaultValues: {
      clientId: '',
      clientSecret: '',
      selectedEnv: ''
    }
  });

  const { handleSubmit, control, watch, setValue } = methods;
  const selectedEnv = watch('selectedEnv');

  const onFinish = async (data) => {
    let { clientId, clientSecret } = data;
    try {
      if (selectedEnv === 'Sandbox' && (!clientId || !clientSecret)) {
        clientId = getDefaultCredentials(selectedEnv).clientId;
        clientSecret = getDefaultCredentials(selectedEnv).secret;
      }
      const { url, audience, grantType } = getDefaultCredentials(selectedEnv);
      setSubmitLoading(true);
      const response = await axios.post(
        url,
        {
          client_id: clientId,
          client_secret: clientSecret,
          grant_type: grantType,
          audience
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      setSubmitLoading(false);

      const propsToPass = {
        env: selectedEnv,
        audience,
        token: response.data.access_token
      };
      navigate('/stores', { state: propsToPass });
    } catch (error) {
      setSubmitLoading(false);
      console.error('Error:', error);
      setOpen(true);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const renderEnvironmentMenuItems = () => {
    if (env === 'dev') {
      return (
        <Select
          value={selectedEnv}
          onChange={(event) => setValue('selectedEnv', event.target.value)}
          label="Select the Environment"
          fullWidth
        >
          <MenuItem value="Dev">Dev</MenuItem>
          <MenuItem value="QA">QA</MenuItem>
          <MenuItem value="UAT">UAT</MenuItem>
          <MenuItem value="Sandbox">Sandbox</MenuItem>
          <MenuItem value="prod">Prod</MenuItem>
        </Select>
      );
    }
    if (env === 'qa') {
      return (
        <Select
          value={selectedEnv}
          onChange={(event) => setValue('selectedEnv', event.target.value)}
          label="Select the Environment"
          fullWidth
        >
          <MenuItem value="QA">QA</MenuItem>
          <MenuItem value="Sandbox">Sandbox</MenuItem>
          <MenuItem value="prod">Prod</MenuItem>
        </Select>
      );
    }
    if (env === 'uat') {
      return (
        <Select
          value={selectedEnv}
          onChange={(event) => setValue('selectedEnv', event.target.value)}
          label="Select the Environment"
          fullWidth
        >
          <MenuItem value="UAT">UAT</MenuItem>
        </Select>
      );
    }
    if (env === 'prod') {
      return (
        <Select
          value={selectedEnv}
          onChange={(event) => setValue('selectedEnv', event.target.value)}
          label="Select the Environment"
          fullWidth
        >
          <MenuItem value="Sandbox">Sandbox</MenuItem>
          <MenuItem value="prod">Prod</MenuItem>
        </Select>
      );
    }
    return null;
  };

  return (
    <Card
      style={{
        backgroundImage:
          'url(https://ui-cms-s3.snapfinance.com/CMS/login/Login_bg_final.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      borderOnHover={false}
      insetBorder={false}
    >
      <div className="stores-login-card">
        <img
          src="/logo.svg"
          alt="Snap Logo"
          height="20"
          width="80"
          id="snapLogo"
        />
        <div className="demoStoresTitle">Sign in to Demo Stores</div>
        <FormProvider
          handleSubmit={handleSubmit}
          control={control}
          setValue={setValue}
          watch={watch}
        >
          <Box mt={3} component="form" onSubmit={handleSubmit(onFinish)}>
            <FormControl fullWidth>
              <InputLabel>Environment</InputLabel>
              {renderEnvironmentMenuItems()}
            </FormControl>
            <Box mt={2}>
              <PasswordFieldControl
                style={{ width: '300px' }}
                name="clientId"
                label="Client ID"
                control={control}
              />
            </Box>
            <Box mt={2}>
              <PasswordFieldControl
                style={{ width: '300px' }}
                name="clientSecret"
                label="Client Secret"
                type="password"
                control={control}
              />
            </Box>
            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                disabled={isSubmitLoading}
                fullWidth
              >
                {isSubmitLoading ? 'Loading...' : 'Continue'}
              </Button>
            </Box>
            <Alert
              severity="info"
              className="index-store-info"
              sx={{
                '& .MuiAlert-icon': {
                  color: '#3D5CCF'
                }
              }}
            >
              <div className="bullet-list">
                <p>
                  In order to integrate Snap Checkout 3.0 you must select an
                  environment and provide your Client ID and Secret.
                </p>
                <p>
                  If Client ID and Secret are not provided, then default
                  credentials will be used.
                </p>
                <p>The JWT token will be generated automatically.</p>
              </div>
            </Alert>
            <div className="index-copyright">
              Copyright &copy; {new Date().getFullYear()} Snap Finance
            </div>
          </Box>
        </FormProvider>
      </div>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={handleClose}
        autoHideDuration={3000}
        message="You are not authorized to login."
      />
    </Card>
  );
}

export default IndexPage;
